import { ReferenceRangeDisplay } from "@/components/ReferenceRangeDisplay/ReferenceRangeDisplay";
import { BodyText, toast } from "@/components/ui";
import {
  HistoryModal,
  type HistoryRow,
} from "@/features/Patient/MedicalRecords/components/HistoryModal";
import { formatDate, formatDateTime } from "@/lib/time";
import type {
  ReferenceRangeCriticality,
  ReferenceRanges,
} from "@/types/observations";
import { Observation as ObservationResource } from "@curai/js-fhir-tools";
import type { Observation } from "fhir/r4";
import React from "react";
import type { VitalDataMap, VitalsType } from "../config/vitalsTypes";
import { VITAL_DISPLAY_NAMES } from "../config/vitalsTypes";
import {
  parseVitalsObservation,
  useVitalsObservationHistory,
  useVoidVitalsObservation,
} from "../network/useVitalsObservation";

export interface VitalHistoryModalProps<T extends VitalsType> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vitalsType: T;
  patientId?: string;
  format: (data: VitalDataMap[T], fallback: string) => string | React.ReactNode;
}

function getInfoItems(observation: Observation) {
  const performedByPatient = observation?.performer?.[0]?.type === "Patient";
  return [
    `Performed by ${observation?.performer?.[0]?.display || "Unknown"} ${performedByPatient ? "(Patient)" : ""} on ${formatDateTime(observation?.issued)}`,
  ];
}

function renderVitalResult(
  formattedResult: string | React.ReactNode,
  criticality: ReferenceRangeCriticality | null | undefined,
  referenceRanges: ReferenceRanges | null | undefined,
): React.ReactNode {
  // Convert ReactNode to string for ReferenceRangeDisplay
  const dataString =
    typeof formattedResult === "string"
      ? formattedResult
      : String(formattedResult);
  return (
    <BodyText size="sm">
      <ReferenceRangeDisplay
        criticality={criticality ?? null}
        data={dataString}
        referenceRanges={referenceRanges ?? null}
        tooltipFallback=""
        tooltipContentClassName="z-30"
      />
    </BodyText>
  );
}

export const VitalHistoryModal = <T extends VitalsType>({
  open,
  onOpenChange,
  vitalsType,
  patientId,
  format,
}: VitalHistoryModalProps<T>) => {
  const {
    data: vitalsHistory,
    isLoading,
    isError,
  } = useVitalsObservationHistory(vitalsType, patientId, open);

  const { mutate: voidObservation, isPending: isVoidingObservation } =
    useVoidVitalsObservation({
      patientId,
      vitalsType,
      onSuccess: () => {
        const vitalName = VITAL_DISPLAY_NAMES[vitalsType];
        toast.success(`${vitalName} observation voided successfully`);
      },
    });

  const rows: HistoryRow[] = React.useMemo(() => {
    const items = vitalsHistory?.observations ?? [];

    return items.map((item) => {
      try {
        const obs = new ObservationResource(item.observation);
        const parsedData = parseVitalsObservation(obs) as VitalDataMap[T];
        const formattedResult = format(parsedData, "No data");
        return {
          id: item.observation.id,
          date: formatDate(item.observationDate),
          result: renderVitalResult(
            formattedResult,
            item.criticality,
            item.referenceRanges,
          ),
          infoItems: getInfoItems(item.observation),
          onVoid: item.observation
            ? () => {
                voidObservation({
                  observationId: item.observation?.id as string,
                });
              }
            : undefined,
          isVoidPending: isVoidingObservation,
        };
      } catch (error) {
        return {
          id: item.observation.id,
          date: formatDate(item.observationDate),
          result: "Error parsing data",
          infoItems: getInfoItems(item.observation),
          onVoid: item.observation
            ? () => {
                voidObservation({
                  observationId: item.observation?.id as string,
                });
              }
            : undefined,
          isVoidPending: isVoidingObservation,
        };
      }
    });
  }, [vitalsHistory, format, voidObservation, isVoidingObservation]);
  return (
    <HistoryModal
      emptyMessage="No vital history found."
      title={VITAL_DISPLAY_NAMES[vitalsType]}
      rows={rows}
      onOpenChange={onOpenChange}
      open={open}
      isLoading={isLoading}
      isError={isError}
    />
  );
};
